import md5 from 'md5';
import { useContext, useMemo } from 'react';
import { ILanguageContext, LanguageContext } from '../context/LanguageContext';

const get = (obj: ILanguageContext['languages'], path: string, defaultValue: string): string => {
  const objRecord = obj as Record<string, Record<string, string>>;
  const travel = (regexp: RegExp) =>
    String.prototype.split
      .call(path, regexp)
      .filter(Boolean)
      .reduce<Record<string, string> | string | null | undefined>(
        (res, key) => (res !== null && res !== undefined && typeof res === 'object' ? ((res as Record<string, string>)[key] as Record<string, string> | string | undefined) : res),
        objRecord as unknown as Record<string, string> | string | null | undefined,
      );
  const result = travel(/[,[\]]+?/) || travel(/[,[\].]+?/);
  return result === undefined ? defaultValue : String(result);
};

const generateKey = (string: string, options: { _key?: string; _context?: string } = {}) => {
  if (options._key) return options._key;

  let context = '';
  if (options._context) {
    context = options._context;
    context = context.replace(/,/g, ':');
  }
  return md5(`5:${string}:${context}`);
};

/**
 * swc = Start with capital
 * cap = Capitalize
 * uppercase = Uppercase
 */
export type useTArgs = {
  args?: { [key: string]: string | number };
  transformer?: (value: string) => string;
  swc?: boolean;
  cap?: boolean;
  uppercase?: boolean;
};
const useT = (e: string, args?: useTArgs) => {
  const { language, languages } = useContext(LanguageContext);

  const hash = generateKey(e);
  const str = get(languages, `${language}.${hash}`, e);

  const translationWithArgs = useMemo(() => {
    try {
      let result = Object.keys(args?.args || {}).reduce((p, c) => p.replace(`{${c}}`, String((args?.args || {})[c])), str);
      if (args?.swc) result = `${result[0]?.toUpperCase()}${result.slice(1)}`;
      if (args?.cap) result = result.replace(/(?:^|\s|["'([{])+\S/g, (match: string) => match.toUpperCase());
      if (args?.uppercase) result = result.toUpperCase();
      if (args?.transformer) result = args.transformer(result);
      return result;
    } catch (error) {
      console.error('useT::error:', error);
      return '';
    }
  }, [args, str]);
  if (!str) return e;
  return translationWithArgs as string;
};

/**
 * A translate function for strings only known at runtime (lists of labels
 * that are translation keys, e.g. built-in category names) — `useT` needs one
 * hook call per string. Same lookup and `swc` handling as `useT`.
 */
export const useTranslator = (): ((e: string, options?: { swc?: boolean }) => string) => {
  const { language, languages } = useContext(LanguageContext);
  return useMemo(
    () => (e: string, options?: { swc?: boolean }) => {
      const str = get(languages, `${language}.${generateKey(e)}`, e) || e;
      return options?.swc ? `${str[0]?.toUpperCase() ?? ''}${str.slice(1)}` : str;
    },
    [language, languages],
  );
};

export default useT;
